import React, { useState, useRef, useEffect } from "react";
import { User } from "../../types";
import { authService } from "../../services/authService";
import { useAuth } from "../../hooks/useAuth";
import { useSettings } from "../../contexts/SettingsContext";
import { usePreferences } from "../../hooks/usePreferences";
import { getUserInitial as getInitial } from "../../utils/formatting";
import { ActivitiesModal } from "../Activities/ActivitiesModal";
import { FriendsModal } from "../Friends/FriendsModal";
import { ConfirmationDialog } from "../Activities/ConfirmationDialog";
import { ToggleSwitch } from "../Settings/ToggleSwitch";
import { friendsService } from "../../services/friendsService";
import { settingsService } from "../../services/settingsService";
import {
  setInstallPromptCallback,
  triggerInstallPrompt,
  clearCachesAndReload,
  prepareUpdate,
} from "../../serviceWorkerRegistration";
import debugx from "debugx";

const debug = debugx("bikedash:user");

// Simple hash function for HTML content detection
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
}

interface UserMenuProps {
  user: User | null;
}

export function UserMenu({ user }: UserMenuProps) {
  const { isGuest } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [serverOnline, setServerOnline] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<
    "online" | "offline" | "checking" | "degraded"
  >("online");
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [installAvailable, setInstallAvailable] = useState(false);
  const [installBadgePulse, setInstallBadgePulse] = useState(true);
  const [updateBadgePulse, setUpdateBadgePulse] = useState(true);
  const [newRequestsAvailable, setNewRequestsAvailable] = useState(false);
  const [newRequestsBadgePulse, setNewRequestsBadgePulse] = useState(true);
  const [friendsModalTab, setFriendsModalTab] = useState<
    "friends" | "invite" | "profile"
  >("friends");
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [showFriendsModal, setShowFriendsModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showInviteWelcome, setShowInviteWelcome] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [shareLocation, setShareLocation] = useState(
    settingsService.getUserPreferences().shareLocationWithFriends,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const preparedVersionRef = useRef<string | null>(null);
  const { openSettings } = useSettings();
  const { userPreferences } = usePreferences();

  // Stop install badge pulse after 20 seconds, restart on page visibility
  useEffect(() => {
    let timer: number | undefined;

    const startTimer = () => {
      if (timer) clearTimeout(timer);
      setInstallBadgePulse(true);
      timer = window.setTimeout(() => {
        setInstallBadgePulse(false);
      }, 20000);
    };

    // Start initial timer
    startTimer();

    // Restart pulse when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden && installAvailable) {
        startTimer();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [installAvailable]);

  // Stop update badge pulse after 20 seconds, restart on page visibility
  useEffect(() => {
    let timer: number | undefined;

    const startTimer = () => {
      if (timer) clearTimeout(timer);
      setUpdateBadgePulse(true);
      timer = window.setTimeout(() => {
        setUpdateBadgePulse(false);
      }, 20000);
    };

    // Start initial timer
    startTimer();

    // Restart pulse when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden && updateAvailable) {
        startTimer();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [updateAvailable]);

  // Stop new requests badge pulse after 20 seconds, restart on page visibility
  useEffect(() => {
    let timer: number | undefined;

    const startTimer = () => {
      if (timer) clearTimeout(timer);
      setNewRequestsBadgePulse(true);
      timer = window.setTimeout(() => {
        setNewRequestsBadgePulse(false);
      }, 20000);
    };

    // Start initial timer
    startTimer();

    // Restart pulse when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden && newRequestsAvailable) {
        startTimer();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [newRequestsAvailable]);

  // Monitor new friend requests
  useEffect(() => {
    if (!user || isGuest) return;

    const unsubscribe = friendsService.onNewRequests((hasNew) => {
      debug("New friend requests status: %s", hasNew);
      setNewRequestsAvailable(hasNew);
    });

    return () => {
      unsubscribe();
    };
  }, [user, isGuest]);

  // Monitor friendsService connection status
  useEffect(() => {
    if (!user || isGuest) return;

    const unsubscribe = friendsService.onStatusChange((status) => {
      debug("FriendsService connection status: %s", status);
      setConnectionStatus(status);
    });

    // Get initial status
    setConnectionStatus(friendsService.getConnectionStatus());

    return () => {
      unsubscribe();
    };
  }, [user, isGuest]);

  // Subscribe to settings changes
  useEffect(() => {
    const unsubscribe = settingsService.subscribe((settings) => {
      setShareLocation(settings.user.shareLocationWithFriends);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Check for invite code in URL and open friends modal or show welcome
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const inviteCode = urlParams.get("invite");

    if (inviteCode && inviteCode.length === 6) {
      if (user && !isGuest) {
        debug("Invite code detected in URL, opening friends modal");
        setFriendsModalTab("invite");
        setShowFriendsModal(true);

        // Remove invite param from URL after handling
        urlParams.delete("invite");
        const newUrl = urlParams.toString()
          ? `${window.location.pathname}?${urlParams.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      } else {
        debug("Invite code detected but user not logged in, showing welcome");
        setShowInviteWelcome(true);

        // Remove invite param from URL after showing welcome
        urlParams.delete("invite");
        const newUrl = urlParams.toString()
          ? `${window.location.pathname}?${urlParams.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [user, isGuest]);

  // Monitor PWA install availability
  useEffect(() => {
    // Check if running as standalone PWA
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;

    if (isStandalone) {
      debug("Already running as PWA");
      setInstallAvailable(false);
      return;
    }

    // iOS Safari doesn't support beforeinstallprompt
    // Detect iOS and show install button
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isIOS || isSafari) {
      debug("iOS/Safari detected - showing install option");
      setInstallAvailable(true);
    } else {
      // For Chrome/Edge, use the standard install prompt
      setInstallPromptCallback((canInstall: boolean) => {
        debug("Install prompt available:", canInstall);
        setInstallAvailable(canInstall);
      });
    }
  }, []);

  // Store the initial HTML hash when app loads
  useEffect(() => {
    const getInitialHash = async () => {
      const lastKnownHash = localStorage.getItem("bikedash_html_hash");
      if (!lastKnownHash) {
        // First time - compute hash of current HTML
        try {
          const response = await fetch(`/?_hash_check=${Date.now()}`, {
            method: "GET",
            cache: "no-cache",
          });
          if (response.ok) {
            const html = await response.text();
            // Simple hash function
            const hash = simpleHash(html);
            localStorage.setItem("bikedash_html_hash", hash.toString());
            debug("Initial HTML hash: %s", hash);
          }
        } catch (error) {
          debug("Failed to get initial HTML hash:", error);
        }
      }
    };
    getInitialHash();
  }, []);

  // Check for updates by comparing HTML hash
  useEffect(() => {
    // Check if we're in dev mode
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    const isDevServer = window.location.hostname.includes("-dev");

    // Skip hash check on localhost - uses network-first anyway
    if (isLocalhost) {
      return;
    }

    const checkInterval = isDevServer ? 1000 : 30000; // 1s on dev server, 30s in prod

    const checkForUpdates = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        // First check server health
        const healthResponse = await fetch("/api/health", {
          method: "GET",
          cache: "no-cache",
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (healthResponse.ok) {
          setServerOnline(true);

          // Now check if HTML has changed
          // Use a query parameter to bypass service worker cache
          const htmlResponse = await fetch(`/?_hash_check=${Date.now()}`, {
            method: "GET",
            cache: "no-cache",
          });

          // Only check hash if we got a valid response (not a 5xx error or Cloudflare error page)
          if (htmlResponse.ok && htmlResponse.status === 200) {
            const html = await htmlResponse.text();

            // Verify this is actually our HTML, not a Cloudflare error page
            // Our HTML always contains the root div and script tags
            const isValidHtml =
              html.includes('<div id="root">') ||
              html.includes('<div id="app">') ||
              html.includes("bikedash");

            if (isValidHtml) {
              const currentHash = simpleHash(html);
              const lastKnownHash = localStorage.getItem("bikedash_html_hash");

              if (lastKnownHash && currentHash.toString() !== lastKnownHash) {
                debug(
                  "HTML changed - old hash: %s, new hash: %s",
                  lastKnownHash,
                  currentHash,
                );
                setServerVersion(currentHash.toString());

                // Always show update banner
                setUpdateAvailable(true);

                // If auto-update is enabled, prepare update in background (only once per version)
                if (userPreferences.autoUpdate ?? true) {
                  if (preparedVersionRef.current !== currentHash.toString()) {
                    debug(
                      "Auto-update enabled, preparing update in background for version:",
                      currentHash,
                    );
                    preparedVersionRef.current = currentHash.toString();
                    // Prepare the update in background (clears caches)
                    prepareUpdate().then(() => {
                      debug("Update prepared successfully, ready to reload");
                    });
                  }
                }
              }
            } else {
              debug(
                "Received invalid HTML (likely error page), ignoring hash check",
              );
              // Don't mark server as offline, but don't check for updates either
            }
          } else if (htmlResponse.status >= 500) {
            debug(
              "Server returned %s error, skipping update check",
              htmlResponse.status,
            );
            // Don't mark server offline if health check passed
          }
        } else if (healthResponse.status >= 500) {
          setServerOnline(false);
        } else {
          setServerOnline(true);
        }
      } catch (error) {
        setServerOnline(false);
      }
    };

    // Check immediately
    checkForUpdates();

    // Check periodically (1s in dev, 30s in prod)
    const interval = setInterval(checkForUpdates, checkInterval);

    debug(
      "Update check interval: %dms (%s mode)",
      checkInterval,
      isDevServer ? "dev" : "prod",
    );

    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  const handleLogin = () => {
    authService.openAuthPopup("login");
    setIsOpen(false);
  };

  const handleLogout = () => {
    authService.logout();
    setIsOpen(false);
  };

  const getUserInitial = () => {
    if (user?.username) {
      return getInitial(user.username);
    }
    return "?";
  };

  const getStatusColor = () => {
    if (!isOnline) return "#666";
    if (!serverOnline) return "#ff9500";
    return "#00d200";
  };

  const getStatusTitle = () => {
    if (!isOnline) return "No internet";
    if (!serverOnline) return "Server offline";
    return "Connected";
  };

  return (
    <div className="user-menu" ref={dropdownRef}>
      <button
        className="user-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={user ? "User menu" : "Sign in"}
      >
        <div
          className="user-avatar"
          style={{
            background: user?.mapColor || undefined,
            fontSize: user?.mapIcon ? "20px" : undefined,
            borderRadius: "50%",
          }}
        >
          {user ? (
            <span className="avatar-initial">
              {user.mapIcon || getUserInitial()}
            </span>
          ) : (
            <span className="guest-icon">👤</span>
          )}

          {/* Install/Update/New Requests indicator badge - prefer friend requests > install > update */}
          {newRequestsAvailable ? (
            <div
              className={`install-badge ${newRequestsBadgePulse ? "pulse" : ""}`}
              title="New friend requests"
            >
              👥
            </div>
          ) : installAvailable ? (
            <div
              className={`install-badge ${installBadgePulse ? "pulse" : ""}`}
              title="Install app available"
            >
              📲
            </div>
          ) : updateAvailable ? (
            <div
              className={`install-badge ${updateBadgePulse ? "pulse" : ""}`}
              title="Update available"
            >
              🔄
            </div>
          ) : null}

          {/* Status indicator dot - show large blue dot if sharing location */}
          <div
            className={`status-dot ${shareLocation ? "location-sharing" : ""}`}
            style={{ background: shareLocation ? "#3b82f6" : getStatusColor() }}
            title={shareLocation ? "Sharing location" : getStatusTitle()}
          />
        </div>
      </button>

      {isOpen && (
        <>
          <div className="menu-overlay" onClick={() => setIsOpen(false)} />
          <div className="menu-dropdown">
            {/* Update notification banner */}
            {updateAvailable && (
              <div className="update-banner">
                <div className="update-content">
                  <span className="update-icon">🔄</span>
                  <span className="update-text">New version available</span>
                </div>
                <div className="update-actions">
                  <button
                    className="update-btn"
                    onClick={async () => {
                      if (serverVersion) {
                        localStorage.setItem(
                          "bikedash_html_hash",
                          serverVersion,
                        );
                      }

                      // Always clear caches and reload to ensure fresh content
                      // If auto-update is enabled, new SW is already activated in background
                      debug(
                        (userPreferences.autoUpdate ?? true)
                          ? "User clicked Update Now - clearing caches and reloading (SW already activated)"
                          : "User clicked Update - clearing caches and reloading",
                      );
                      await clearCachesAndReload();
                    }}
                  >
                    {(userPreferences.autoUpdate ?? true)
                      ? "Update Now"
                      : "Update"}
                  </button>
                  <button
                    className="dismiss-btn"
                    onClick={() => {
                      if (serverVersion) {
                        localStorage.setItem(
                          "bikedash_html_hash",
                          serverVersion,
                        );
                      }
                      setUpdateAvailable(false);
                    }}
                    title="Dismiss"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* Install app banner */}
            {installAvailable && !updateAvailable && (
              <div className="update-banner">
                <div className="update-content">
                  <span className="update-icon">📲</span>
                  <span className="update-text">Install BikeDash app</span>
                </div>
                <div className="update-actions">
                  <button
                    className="update-btn"
                    onClick={async () => {
                      // Detect iOS/Safari
                      const isIOS =
                        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
                        !(window as any).MSStream;
                      const isSafari = /^((?!chrome|android).)*safari/i.test(
                        navigator.userAgent,
                      );

                      if (isIOS || isSafari) {
                        // Open Help settings tab with install instructions
                        openSettings("help");
                        setIsOpen(false);
                      } else {
                        // Use standard Chrome/Edge install prompt
                        const result = await triggerInstallPrompt();
                        debug("Install prompt result:", result);
                        if (result === "accepted") {
                          setInstallAvailable(false);
                        }
                      }
                    }}
                  >
                    Install
                  </button>
                  <button
                    className="dismiss-btn"
                    onClick={() => setInstallAvailable(false)}
                    title="Dismiss"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* New friend requests banner - show in addition to install/update */}
            {newRequestsAvailable && user && !isGuest && (
              <div className="update-banner">
                <div className="update-content">
                  <span className="update-icon">👥</span>
                  <span className="update-text">New friend requests</span>
                </div>
                <div className="update-actions">
                  <button
                    className="update-btn"
                    onClick={() => {
                      friendsService.dismissNewRequests();
                      setFriendsModalTab("friends");
                      setShowFriendsModal(true);
                      setNewRequestsAvailable(false);
                      setIsOpen(false);
                    }}
                  >
                    View
                  </button>
                  <button
                    className="dismiss-btn"
                    onClick={() => {
                      friendsService.dismissNewRequests();
                      setNewRequestsAvailable(false);
                    }}
                    title="Dismiss"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* User info section */}
            <div className="menu-header">
              {/* Close button */}
              <button
                className="menu-header-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close menu"
                title="Close"
              >
                ×
              </button>

              {user ? (
                <>
                  <div
                    className="user-details"
                    onClick={() => {
                      if (!isGuest && serverOnline) {
                        setFriendsModalTab("profile");
                        setShowFriendsModal(true);
                        setIsOpen(false);
                      }
                    }}
                    style={{
                      cursor: isGuest || !serverOnline ? "default" : "pointer",
                    }}
                  >
                    <div
                      className="user-avatar-large"
                      style={{
                        background: user.mapColor || undefined,
                        fontSize: user.mapIcon ? "32px" : undefined,
                      }}
                    >
                      <span>{user.mapIcon || getUserInitial()}</span>
                    </div>
                    <div className="user-info">
                      <div className="user-name">{user.username}</div>
                      <div className="user-email">
                        {isGuest ? "Guest Mode" : user.email}
                      </div>
                    </div>
                  </div>

                  {/* Status grid */}
                  <div className="status-grid">
                    <div className="status-item">
                      <div className="status-label">Status</div>
                      <div
                        className={`status-value ${!isOnline ? "offline" : !serverOnline ? "warning" : connectionStatus === "degraded" ? "warning" : "online"}`}
                        onClick={() => {
                          if (connectionStatus === "degraded") {
                            setShowErrorDialog(true);
                            setIsOpen(false);
                          }
                        }}
                        style={{
                          cursor:
                            connectionStatus === "degraded"
                              ? "pointer"
                              : "default",
                        }}
                      >
                        {!isOnline
                          ? "● Offline"
                          : !serverOnline
                            ? "⚠ Server Offline"
                            : connectionStatus === "degraded"
                              ? "⚠ Degraded"
                              : "● Connected"}
                      </div>
                    </div>
                    <div
                      className="status-item status-item-toggle"
                      onClick={() => {
                        const newValue = !shareLocation;
                        settingsService.updateUserPreferences({
                          shareLocationWithFriends: newValue,
                        });
                        debug(
                          "Location sharing %s",
                          newValue ? "enabled" : "disabled",
                        );
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <label className="status-label-clickable">
                        Share Location
                      </label>
                      <div className="status-value">
                        <ToggleSwitch
                          checked={!!shareLocation}
                          onChange={(checked) => {
                            settingsService.updateUserPreferences({
                              shareLocationWithFriends: checked,
                            });
                            debug(
                              "Location sharing %s",
                              checked ? "enabled" : "disabled",
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="guest-state">
                  <div className="guest-avatar">👤</div>
                  <div className="guest-message">
                    {!serverOnline ? "Server Offline" : "Not logged in"}
                  </div>
                  <div className="guest-submessage">
                    {!isOnline
                      ? "Offline mode"
                      : !serverOnline
                        ? "Server unreachable"
                        : "Sign in to sync activities and access more features"}
                  </div>
                </div>
              )}
            </div>

            {/* Menu actions */}
            <div className="menu-items">
              <button
                className="menu-item"
                onClick={() => {
                  openSettings();
                  setIsOpen(false);
                }}
              >
                <span className="menu-icon">⚙️</span>
                <span>Settings</span>
              </button>
              <button
                className="menu-item"
                onClick={() => {
                  setShowActivitiesModal(true);
                  setIsOpen(false);
                }}
              >
                <span className="menu-icon">📊</span>
                <span>Activities</span>
              </button>
              {user && !isGuest && (
                <button
                  className="menu-item"
                  onClick={() => {
                    setShowFriendsModal(true);
                    setIsOpen(false);
                  }}
                  disabled={!serverOnline}
                >
                  <span className="menu-icon">👥</span>
                  <span>Friends</span>
                </button>
              )}
              <button
                className="menu-item"
                onClick={() => {
                  openSettings("help");
                  setIsOpen(false);
                }}
              >
                <span className="menu-icon">❓</span>
                <span>Help</span>
              </button>
              <hr className="menu-divider" />
              {user ? (
                <button
                  className="menu-item logout"
                  onClick={() => setShowLogoutConfirm(true)}
                  disabled={!serverOnline}
                >
                  <span className="menu-icon">🚪</span>
                  <span>Sign Out</span>
                </button>
              ) : (
                <button
                  className="menu-item primary"
                  onClick={handleLogin}
                  disabled={!isOnline || !serverOnline}
                >
                  <span className="menu-icon">
                    {!isOnline ? "📴" : !serverOnline ? "⚠️" : "🔐"}
                  </span>
                  <span>
                    {!isOnline
                      ? "Offline"
                      : !serverOnline
                        ? "Server Offline"
                        : "Sign In"}
                  </span>
                </button>
              )}
            </div>
          </div>
        </>
      )}

      <ActivitiesModal
        isOpen={showActivitiesModal}
        onClose={() => setShowActivitiesModal(false)}
      />

      <FriendsModal
        isOpen={showFriendsModal}
        onClose={() => {
          setShowFriendsModal(false);
          setFriendsModalTab("friends");
        }}
        initialTab={friendsModalTab}
      />

      <ConfirmationDialog
        visible={showLogoutConfirm}
        title="Sign Out"
        message="Are you sure you want to sign out?"
        confirmText="Sign Out"
        cancelText="Cancel"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          handleLogout();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />

      <ConfirmationDialog
        visible={showInviteWelcome}
        title="Friend Invite"
        message="You've been invited to connect on BikeDash! Please sign in first, then follow this link again to accept the invitation."
        confirmText="Sign In"
        cancelText="Later"
        onConfirm={() => {
          setShowInviteWelcome(false);
          handleLogin();
        }}
        onCancel={() => setShowInviteWelcome(false)}
      />

      <ConfirmationDialog
        visible={showErrorDialog}
        title="Service Error"
        message={(() => {
          const lastError = friendsService.getLastError();
          if (lastError) {
            const timeAgo = Math.round(
              (Date.now() - lastError.timestamp) / 1000,
            );
            const timeStr =
              timeAgo < 60
                ? `${timeAgo} seconds ago`
                : `${Math.round(timeAgo / 60)} minutes ago`;
            return `${lastError.message}\n\nOccurred ${timeStr}`;
          }
          return "An error occurred with the friends service.";
        })()}
        confirmText="OK"
        onConfirm={() => setShowErrorDialog(false)}
        onCancel={() => setShowErrorDialog(false)}
      />
    </div>
  );
}
